/**
 * Error Boundary Component
 * Catches and handles React component errors gracefully
 */

import React, { Component } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { handleError } from '../../utils/errorHandler.ts';
/**
 * Error Boundary Component
 */
export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }
  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      error,
      errorInfo: null
    };
  }
  componentDidCatch(error, errorInfo) {
    // Log error
    handleError(error, {
      showToast: false,
      logToConsole: true,
      severity: 'critical'
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Update state
    this.setState({
      errorInfo
    });
  }
  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };
  handleGoHome = () => {
    window.location.href = '/';
  };
  render() {
    if (this.state.hasError) {
      // Custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        bgcolor: 'background.default',
        p: 3
      }}>
          <Card sx={{
          maxWidth: 600,
          width: '100%',
          textAlign: 'center'
        }}>
            <CardContent sx={{
            p: 4
          }}>
              <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: 3
            }}>
                <Box sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: 'error.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: 0.1
              }}>
                  <AlertTriangle size={40} color="#D32F2F" />
                </Box>
              </Box>

              <Typography variant="h2" sx={{
              mb: 2,
              color: 'text.primary'
            }}>
                Something went wrong
              </Typography>

              <Typography variant="body1" sx={{
              mb: 3,
              color: 'text.secondary'
            }}>
                We're sorry for the inconvenience. An unexpected error has occurred.
              </Typography>

              {/* Only show error details in development mode - never in production */}
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <Box sx={{
                  textAlign: 'left',
                  bgcolor: 'background.default',
                  p: 2,
                  borderRadius: 1,
                  mb: 3,
                  maxHeight: 200,
                  overflow: 'auto'
                }}>
                  <Typography variant="body2" sx={{
                    fontFamily: 'monospace',
                    color: 'error.main',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }}>
                    {this.state.error.toString()}
                  </Typography>
                  {this.state.errorInfo && (
                    <Typography variant="body2" sx={{
                      fontFamily: 'monospace',
                      color: 'text.secondary',
                      fontSize: '0.75rem',
                      mt: 1,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word'
                    }}>
                      {this.state.errorInfo.componentStack}
                    </Typography>
                  )}
                </Box>
              )}

              <Box sx={{
              display: 'flex',
              gap: 2,
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
                <Button variant="contained" startIcon={<RefreshCw size={20} />} onClick={this.handleReset} sx={{
                minWidth: 140
              }}>
                  Try Again
                </Button>
                <Button variant="outlined" startIcon={<Home size={20} />} onClick={this.handleGoHome} sx={{
                minWidth: 140
              }}>
                  Go Home
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>;
    }
    return this.props.children;
  }
}

/**
 * Hook for functional components to handle errors
 */
export const useErrorHandler = () => {
  const [error, setError] = React.useState(null);
  React.useEffect(() => {
    if (error) {
      throw error;
    }
  }, [error]);
  return setError;
};